import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronUp,
  Search,
  RefreshCw,
  Copy,
  Check,
  AlertCircle,
  Code,
  Database,
  FileText,
  Terminal,
  Save,
  X as XIcon
} from 'lucide-react';
import type { User } from '../../types';

interface DeploymentInstruction {
  id: string;
  moduleName: string;
  category: 'new-module' | 'feature' | 'bug-fix' | 'update' | 'migration';
  steps: InstructionStep[];
  createdBy: User;
  createdAt: string;
  updatedAt: string;
  version: number;
  tags?: string[];
}

interface InstructionStep {
  id: string;
  title: string;
  description: string;
  type: 'files' | 'code' | 'command' | 'database' | 'config';
  content: string;
  notes?: string;
}

interface DeploymentInstructionsProps {
  currentUser: User;
}

interface EditingInstruction extends Partial<DeploymentInstruction> {
  isNew?: boolean;
}

const DeploymentInstructions: React.FC<DeploymentInstructionsProps> = ({ currentUser }) => {
  const [instructions, setInstructions] = useState<DeploymentInstruction[]>([]);
  const [filteredInstructions, setFilteredInstructions] = useState<DeploymentInstruction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingInstruction, setEditingInstruction] = useState<EditingInstruction | null>(null);
  const [copiedStepId, setCopiedStepId] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);

  // Check if user can edit/delete
  const canEdit = ['pm', 'tl', 'devops', 'admin'].includes(currentUser.role);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'new-module', label: 'New Module' },
    { value: 'feature', label: 'Feature' },
    { value: 'bug-fix', label: 'Bug Fix' },
    { value: 'update', label: 'Update' },
    { value: 'migration', label: 'Migration' }
  ];

  const stepTypes = [
    { value: 'files', label: 'Files to Add', icon: FileText },
    { value: 'code', label: 'Code Changes', icon: Code },
    { value: 'command', label: 'Commands', icon: Terminal },
    { value: 'database', label: 'Database Changes', icon: Database },
    { value: 'config', label: 'Configuration', icon: FileText }
  ];

  useEffect(() => {
    fetchInstructions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [instructions, search, selectedCategory]);

  const fetchInstructions = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch('/api/deployment-instructions');
      if (!response.ok) throw new Error('Failed to fetch instructions');

      const data = await response.json();
      setInstructions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch instructions');
      console.error('Error fetching instructions:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...instructions];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(i => i.category === selectedCategory);
    }

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(i =>
        i.moduleName.toLowerCase().includes(searchLower) ||
        i.steps.some(s => s.title.toLowerCase().includes(searchLower))
      );
    }

    setFilteredInstructions(filtered);
  };

  const handleDeleteStep = (instructionId: string, stepId: string) => {
    setInstructions(instructions.map(i =>
      i.id === instructionId
        ? { ...i, steps: i.steps.filter(s => s.id !== stepId) }
        : i
    ));
  };

  const handleDeleteInstruction = async (id: string) => {
    if (!canEdit) return;

    try {
      const response = await fetch(`/api/deployment-instructions/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete instruction');

      setInstructions(instructions.filter(i => i.id !== id));
      setSuccess('Instruction deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete instruction');
    }
  };

  const handleSaveInstruction = async () => {
    if (!editingInstruction || !editingInstruction.moduleName) {
      setError('Module name is required');
      return;
    }

    try {
      if (editingInstruction.isNew) {
        // Create new instruction via API
        const response = await fetch('/api/deployment-instructions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            moduleName: editingInstruction.moduleName,
            category: editingInstruction.category || 'feature',
            steps: editingInstruction.steps || [],
            createdBy: currentUser,
            tags: editingInstruction.tags || []
          })
        });

        if (!response.ok) throw new Error('Failed to create instruction');

        const newInstruction = await response.json();
        setInstructions([...instructions, newInstruction]);
        setSuccess('Instruction created successfully');
      } else if (editingId) {
        // Update existing instruction via API
        const response = await fetch(`/api/deployment-instructions/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            moduleName: editingInstruction.moduleName,
            category: editingInstruction.category,
            steps: editingInstruction.steps,
            updatedBy: currentUser,
            tags: editingInstruction.tags || []
          })
        });

        if (!response.ok) throw new Error('Failed to update instruction');

        const updated = await response.json();
        setInstructions(instructions.map(i => i.id === editingId ? updated : i));
        setSuccess('Instruction updated successfully');
      }

      setEditingId(null);
      setEditingInstruction(null);
      setShowNewForm(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save instruction');
    }
  };

  const copyToClipboard = (text: string, stepId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedStepId(stepId);
    setTimeout(() => setCopiedStepId(null), 2000);
  };

  const getStepIcon = (type: string) => {
    const stepType = stepTypes.find(st => st.value === type);
    return stepType ? <stepType.icon className="w-5 h-5" /> : <FileText className="w-5 h-5" />;
  };

  const getStepColor = (type: string) => {
    const colors: Record<string, string> = {
      files: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700',
      code: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700',
      command: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700',
      database: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700',
      config: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
    };
    return colors[type] || colors.files;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Deployment Instructions</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Step-by-step guides for deploying modules</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchInstructions}
            disabled={loading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canEdit && (
            <button
              onClick={() => {
                setShowNewForm(true);
                setEditingInstruction({
                  moduleName: '',
                  category: 'feature',
                  steps: [],
                  isNew: true
                });
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Instruction
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
          <p className="text-green-700 dark:text-green-400">{success}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
        <div className="flex gap-4 flex-wrap">
          {/* Search */}
          <div className="flex-1 min-w-xs relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search instructions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          Found {filteredInstructions.length} instruction{filteredInstructions.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* New/Edit Instruction Form */}
      {(showNewForm || editingId) && editingInstruction && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {editingInstruction.isNew ? 'Create New Instruction' : 'Edit Instruction'}
            </h2>
            <button
              onClick={() => {
                setEditingId(null);
                setEditingInstruction(null);
                setShowNewForm(false);
              }}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Module Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Module Name *
              </label>
              <input
                type="text"
                value={editingInstruction.moduleName || ''}
                onChange={(e) => setEditingInstruction({ ...editingInstruction, moduleName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Fees Module"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category
              </label>
              <select
                value={editingInstruction.category || 'feature'}
                onChange={(e) => setEditingInstruction({ ...editingInstruction, category: e.target.value as any })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.filter(c => c.value !== 'all').map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Steps */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Steps
              </label>
              <div className="space-y-3">
                {editingInstruction.steps?.map((step, idx) => (
                  <div key={step.id} className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900 dark:text-white">Step {idx + 1}</h4>
                      <button
                        onClick={() => handleDeleteStep(editingInstruction.id || '', step.id)}
                        className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <input
                      type="text"
                      placeholder="Step title"
                      value={step.title}
                      onChange={(e) => {
                        const newSteps = editingInstruction.steps?.map(s =>
                          s.id === step.id ? { ...s, title: e.target.value } : s
                        );
                        setEditingInstruction({ ...editingInstruction, steps: newSteps });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />

                    <textarea
                      placeholder="Step content"
                      value={step.content}
                      onChange={(e) => {
                        const newSteps = editingInstruction.steps?.map(s =>
                          s.id === step.id ? { ...s, content: e.target.value } : s
                        );
                        setEditingInstruction({ ...editingInstruction, steps: newSteps });
                      }}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  const newStep: InstructionStep = {
                    id: Date.now().toString(),
                    title: '',
                    description: '',
                    type: 'files',
                    content: ''
                  };
                  setEditingInstruction({
                    ...editingInstruction,
                    steps: [...(editingInstruction.steps || []), newStep]
                  });
                }}
                className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                + Add Step
              </button>
            </div>

            {/* Save/Cancel */}
            <div className="flex gap-2 justify-end pt-4">
              <button
                onClick={() => {
                  setEditingId(null);
                  setEditingInstruction(null);
                  setShowNewForm(false);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveInstruction}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Instruction
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      )}

      {/* Instructions List */}
      {!loading && filteredInstructions.length > 0 && (
        <div className="space-y-4">
          {filteredInstructions.map((instruction) => (
            <div
              key={instruction.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
            >
              {/* Header */}
              <div
                onClick={() => setExpandedId(expandedId === instruction.id ? null : instruction.id)}
                className="cursor-pointer p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{instruction.moduleName}</h2>
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full border ${getStepColor(instruction.category)}`}>
                      {instruction.category.replace('-', ' ')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {instruction.steps.length} step{instruction.steps.length !== 1 ? 's' : ''} • Created by {instruction.createdBy.name}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {canEdit && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingId(instruction.id);
                          setEditingInstruction(instruction);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Are you sure you want to delete this instruction?')) {
                            handleDeleteInstruction(instruction.id);
                          }
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </>
                  )}

                  {expandedId === instruction.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedId === instruction.id && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-6 space-y-4">
                  {instruction.steps.length === 0 ? (
                    <p className="text-gray-600 dark:text-gray-400">No steps defined yet</p>
                  ) : (
                    instruction.steps.map((step, idx) => (
                      <div
                        key={step.id}
                        className={`border-l-4 border rounded-lg p-4 ${getStepColor(step.type)}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {getStepIcon(step.type)}
                              <h3 className="font-semibold text-gray-900 dark:text-white">{step.title}</h3>
                              <span className="text-xs font-medium opacity-75">Step {idx + 1}</span>
                            </div>

                            {step.description && (
                              <p className="text-sm mb-3 opacity-90">{step.description}</p>
                            )}

                            <div className="bg-gray-900 dark:bg-gray-900 rounded p-3 mb-2 overflow-x-auto">
                              <pre className="text-gray-100 text-xs font-mono whitespace-pre-wrap break-words">
                                {step.content}
                              </pre>
                            </div>

                            {step.notes && (
                              <p className="text-xs mt-2 italic opacity-80">📝 Note: {step.notes}</p>
                            )}
                          </div>

                          <button
                            onClick={() => copyToClipboard(step.content, step.id)}
                            className="flex-shrink-0 p-2 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                            title="Copy to clipboard"
                          >
                            {copiedStepId === step.id ? (
                              <Check className="w-5 h-5" />
                            ) : (
                              <Copy className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredInstructions.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-4">No instructions found</p>
          {canEdit && (
            <button
              onClick={() => {
                setShowNewForm(true);
                setEditingInstruction({
                  moduleName: '',
                  category: 'feature',
                  steps: [],
                  isNew: true
                });
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Create first instruction
            </button>
          )}
        </div>
      )}

      {/* Permission Info */}
      {!canEdit && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            View-only access. Contact PM, TL, or DevOps team to create or edit instructions.
          </p>
        </div>
      )}
    </div>
  );
};

export default DeploymentInstructions;
